import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Observable } from 'rxjs';
import { debounceTime, map, startWith, tap } from 'rxjs/operators';
import { isEqual } from 'lodash';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-auto-complete',
  templateUrl: './auto-complete.component.html',
  styleUrls: ['./auto-complete.component.scss']
})
export class AutoCompleteComponent implements OnInit, OnChanges {

  @Input() cssClass: string = '';
  @Input() label: string = '';
  @Input() floatLabel: 'always' | 'auto' = 'auto';
  @Input() inputId = '';
  @Input() placeHolder: string = '';
  @Input() options: any[] = [];
  @Input() excludedOptions: any[] = [];
  @Input() valueProperty: string = '';
  @Input() textProperty: string = '';
  @Input() searchProperties: string[] = [];
  @Input() selectedOptionsLoading = false;
  @Input() showActionsButtons = false;
  @Input() showEditButtons = false;
  @Input() showDeleteButtons = false;
  @Input() enableAddAction = false;
  @Input() infoText = '';
  @Input() formSubmitted = false;
  @Input() required = false;
  @Input() requiredText = '';
  @Input() matErrorId = '';
  @Input() textSeparator = '';
  @Input() additionalTextProperty = '';
  /** Render the additional text as a muted line under the option instead of inline after it */
  @Input() metaAsSubtitle = false;
  @Input() hint = '';
  @Input() showRequiredStatusInLabel: boolean = false;
  @Input() enablePinnedCreateNewOption: boolean = false;
  @Input() errorStateMatcher: ErrorStateMatcher;

  _selectedOption: any;
  get selectedOption() { return this._selectedOption; }
  @Input()
  set selectedOption(value) {
    this._selectedOption = value;
    this.autoCompleteControl.setValue(value);
  }

  _disabled = false;
  get disabled(): boolean { return this._disabled; }
  @Input() set disabled(value: boolean) {
    this._disabled = value;
    this._disabled ? this.autoCompleteControl.disable() : this.autoCompleteControl.enable();
  };

  @Output() searchCompleted = new EventEmitter<any>();
  @Output() inputCleared = new EventEmitter<boolean>();
  @Output() editButtonClicked = new EventEmitter<any>();
  @Output() deleteButtonClicked = new EventEmitter<any>();
  @Output() createNewClicked = new EventEmitter<void>();


  autoCompleteControl = new FormControl();
  filteredOptions?: Observable<any[]>;
  prompt = '" doesn\'t exist';
  noOptions = false;

  constructor(@Inject(DOCUMENT) private document: Document, defaultErrorStateMatcher: ErrorStateMatcher) {
    this.errorStateMatcher = defaultErrorStateMatcher;
  }

  ngOnInit() {
    this.setFilterdOptions();

    if (this.searchProperties.indexOf(this.textProperty) === -1) {
      this.searchProperties.push(this.textProperty);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.setFilterdOptions();
      this.updateValidity();
    }

    if (changes['required']) {
      this.updateValidity();
    }
  }

  displayFn(option: any): string {
    return (option && option[this.textProperty]) ? option[this.textProperty] : '';
  }

  onTextChange(event: Event) {
    event.stopPropagation();
    if (this.selectedOption) {
      const value = this.autoCompleteControl.value;
      if (typeof value === 'string' && (!value || (this.selectedOption[this.textProperty] !== value))) {
        this.inputCleared.emit(true);
      }
    }
  }

  onAutocompleteOpened() {
    this.document.documentElement.classList.add('overlay-no-scroll')
  }

  onAutocompleteClosed() {
    this.document.documentElement.classList.remove('overlay-no-scroll')
  }

  onCreateNewClicked(): void {
    this.createNewClicked.emit();
  }

  onOptionSelected(value: any) {
    if (value) {
      value[this.textProperty] = this.removePromptFromTextProperty(value[this.textProperty]);
      this.selectedOption = value;
      this.searchCompleted.emit(value);
    }
  }

  onEditOption(option: any) {
    if (option) {
      this.editButtonClicked.emit(option);
    }
  }

  onDeleteOption(option: any) {
    if (option) {
      this.deleteButtonClicked.emit(option);
    }
  }

  onClear(event: any) {
    if (event) {
      this.autoCompleteControl.setValue(null);
      this.inputCleared.emit(true);
    }
  }

  private setFilterdOptions() {
    this.filteredOptions = this.autoCompleteControl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        map(value => typeof value === 'string' ? value : value && value[this.textProperty]),
        tap(_ => this.noOptions = false),
        map(queryText => queryText ? this._filter(queryText) : this.options.slice(0, 100))
      );
  }

  private _filter(queryText: string): any[] {
    const filterValue = queryText?.toLowerCase();
    const result = this.options.filter(option => this.searchProperties.some(property => option[property]?.toLowerCase()?.includes(filterValue)))
      .slice(0, 100);
    this.noOptions = result.length === 0;
    if (this.enableAddAction && this.noOptions) {
      const newOption = {
        [this.textProperty]: '"' + queryText + this.prompt
      };
      result.push(newOption);
    }
    this.updateValidity();
    return result;
  }

  private removePromptFromTextProperty(textProperty: string) {
    if (textProperty?.endsWith(this.prompt)) {
      textProperty = textProperty.substring(1, textProperty.indexOf(this.prompt));
    }
    return textProperty;
  }

  private updateValidity() {
    const allOptions = [...this.options, ...this.excludedOptions];
    this.autoCompleteControl.setValidators(forbiddenOptionsValidator(allOptions, this.required, this.valueProperty));
    this.autoCompleteControl.updateValueAndValidity();
  }
}

function forbiddenOptionsValidator(options: any[], required: boolean = false, valueProperty = ''): ValidatorFn {
  // Identity / value-property lookups are O(1); the deep `isEqual` scan is the last resort
  const byValue = valueProperty ? new Map(options.map(option => [option?.[valueProperty], option])) : undefined;
  const identities = new Set(options);
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value;
    // If empty and required, return required error
    if (!value) {
      return required ? { required: true } : null;
    }

    // Check if the value matches one of the options
    if (identities.has(value)) return null;
    if (byValue && value?.[valueProperty] !== undefined) {
      const candidate = byValue.get(value[valueProperty]);
      if (candidate !== undefined && isEqual(candidate, value)) return null;
    }
    const index = options?.findIndex(option => isEqual(option, value));
    return index < 0 ? { required: true } : null;
  };
}
